import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import EditEmployeeForm from '../components/EditEmployeeForm';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../config/permissions';

export default function EmployeeDetailsPage() {
    const { serverId, employeeId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('personal');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const { userRole } = useAuth();
    const canEdit = hasPermission(userRole, 'CAN_EDIT_DATA');
    
    // 1. Listen for Edit Signal
    useEffect(() => {
        if (location.state?.openEditModal) {
            setIsEditModalOpen(true);
            window.history.replaceState({}, document.title);
        }
    }, [location]);

    useEffect(() => {
        async function fetchEmployee() {
            try {
                // Path: servers -> {serverId} -> employees -> {employeeId}
                const docRef = doc(db, 'servers', serverId, 'employees', employeeId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    // Merge the ID with the data so the Edit Form can find it
                    setEmployee({ id: docSnap.id, ...docSnap.data() });
                } else {
                    console.error("No such employee!");
                }
            } catch (error) {
                console.error("Error fetching employee:", error);
            } finally {
                setLoading(false);
            }
        }
        fetchEmployee();
    }, [serverId, employeeId]);

    if (loading) return <div style={{ padding: 20 }}>Loading Profile...</div>;
    if (!employee) return <div style={{ padding: 20 }}>Employee not found.</div>;

    // Helper: Handles safe access to deep properties
    // Checks for both lowercase 'vehicleDetails' and Capital 'VehicleDetails' as seen in your data
    const vehicleData = employee.vehicleDetails || employee.VehicleDetails || {};
    const addressData = employee.address || {};

    const Field = ({ label, value, isLink = false }) => (
        <div style={{ marginBottom: '15px', minWidth: '200px', flex: '1' }}>
            <label style={{ display: 'block', fontSize: '11px', textTransform: 'uppercase', color: '#888', marginBottom: '4px', fontWeight: 'bold', letterSpacing: '0.5px' }}>{label}</label>
            {isLink && value ? (
                <a href={value} target="_blank" rel="noreferrer" style={{ color: '#0056b3', textDecoration: 'underline' }}>View Document</a>
            ) : (
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', wordBreak: 'break-word' }}>{value || '-'}</div>
            )}
        </div>
    );

    const Section = ({ title, children }) => (
        <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            padding: '25px', 
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #f0f2f5', paddingBottom: '10px', fontSize: '16px' }}>{title}</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                {children}
            </div>
        </div>
    );

    const BooleanChip = ({ label, value }) => {
        const isYes = value === "Yes" || value === true;
        return (
            <div style={{ 
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
                padding: '8px 12px', border: '1px solid #eee', borderRadius: '4px', width: '220px' 
            }}>
                <span style={{ fontSize: '13px', fontWeight: '500' }}>{label}</span>
                <span style={{ 
                    fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold',
                    backgroundColor: isYes ? '#e6f4ea' : '#fce8e6', 
                    color: isYes ? '#1e7e34' : '#c0392b' 
                }}>
                    {value || 'No'}
                </span>
            </div>
        );
    };

    return (
        <div style={{ padding: '30px', backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px', backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <button onClick={() => navigate(-1)} style={{ marginRight: '20px', padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer' }}>← Back</button>
                <img src={employee.profilePicture || 'https://via.placeholder.com/150'} alt="Profile" style={{ width: '64px', height: '64px', borderRadius: '50%', marginRight: '20px', objectFit: 'cover', border: '2px solid #eee' }} />
                <div>
                    <h1 style={{ margin: 0, fontSize: '22px', color: '#2c3e50' }}>{employee.fullName}</h1>
                    <p style={{ margin: '5px 0 0', color: '#666' }}>{employee.jobRole || employee.type || 'Staff Member'} • {employee.username}</p>
                </div>
                {canEdit && (<button onClick={() => setIsEditModalOpen(true)} style={{ marginLeft: 'auto', padding: '10px 20px', backgroundColor: '#0056b3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Edit Profile</button>)}
            </div>

            {/* Navigation Tabs */}
            <div style={{ display: 'flex', gap: '5px', marginBottom: '20px', flexWrap: 'wrap' }}>
                {['personal', 'contact', 'employment', 'financial', 'skills', 'travel', 'recruitment', 'compliance'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        style={{
                            padding: '10px 15px', border: 'none', borderRadius: '4px', cursor: 'pointer', textTransform: 'capitalize', fontWeight: '500',
                            backgroundColor: activeTab === tab ? '#0056b3' : 'transparent',
                            color: activeTab === tab ? 'white' : '#555'
                        }}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* --- TAB CONTENT --- */}
            {activeTab === 'personal' && (
                <Section title="Personal Information">
                    <Field label="Full Name" value={employee.fullName} />
                    <Field label="Known As" value={employee.knownAs} />
                    <Field label="Date of Birth" value={employee.dob} />
                    <Field label="Gender" value={employee.gender} />
                    <Field label="Nationality" value={employee.nationality} />
                    <Field label="Ethnicity" value={employee.ethnicity} />
                    <Field label="Marital Status" value={employee.maritalStatus} />
                    <Field label="Religion" value={employee.religion} />
                    <Field label="Sexual Orientation" value={employee.sexualOrientation} />
                    <Field label="Languages" value={employee.knownLanguages?.join(', ')} />
                </Section>
            )}

            {activeTab === 'contact' && (
                <>
                <Section title="Address Details">
                    {/* Explicitly broken down as requested */}
                    <Field label="First Line" value={addressData.firstLineOfAddress} />
                    <Field label="Second Line" value={addressData.secondLineOfAddress} />
                    <Field label="Town/City" value={addressData.town} />
                    <Field label="County" value={addressData.county} />
                    <Field label="Postcode" value={addressData.postalCode} />
                    <Field label="Country" value={addressData.country} />
                    <Field label="Full String" value={addressData.fullAddress} />
                </Section>
                <Section title="Contact Info">
                    <Field label="Email" value={employee.email} />
                    <Field label="Primary Phone" value={`${employee.phoneCode || ''} ${employee.phoneNumber || ''}`} />
                    <Field label="Secondary Phone" value={employee.secondaryTel} />
                </Section>
                <Section title="Next of Kin">
                    <Field label="Name" value={employee.nextOfKin?.name} />
                    <Field label="Relation" value={employee.nextOfKin?.relation} />
                    <Field label="Phone" value={employee.nextOfKin?.phone} />
                    <Field label="Email" value={employee.nextOfKin?.email} />
                    <Field label="Address" value={employee.nextOfKin?.address} />
                </Section>
                </>
            )}

            {activeTab === 'employment' && (
                <Section title="Employment Status">
                    <Field label="Job Role / Type" value={employee.type} />
                    <Field label="Employment Status" value={employee.employeeDetails?.employmentStatus} />
                    <Field label="Contract Type" value={employee.employeeDetails?.contractType} />
                    <Field label="Start Date" value={employee.startDate} />
                    <Field label="Working Hours" value={employee.workingHours} />
                    <Field label="Tax Code" value={employee.taxCode} />
                    <Field label="Commuter Type" value={employee.employeeDetails?.commuterType} />
                </Section>
            )}

            {activeTab === 'financial' && (
                <>
                <Section title="Bank Details">
                    <Field label="Bank Name" value={employee.bankDetails?.bank} />
                    <Field label="Account Name" value={employee.bankDetails?.accountName} />
                    <Field label="Account Number" value={employee.bankDetails?.accountNumber} />
                    <Field label="Sort Code" value={employee.bankDetails?.sortCode} />
                    <Field label="Branch Town" value={employee.bankDetails?.branchTown} />
                </Section>
                <Section title="Pension & Loans">
                    <Field label="NI Number" value={employee.niNumber} />
                    <Field label="Pension Opt-In" value={employee.pension?.pensionOptIn} />
                    <Field label="Pension ID" value={employee.pension?.pensionID} />
                    <Field label="Student Loan" value={employee.studentLoan?.studentLoanOptIn} />
                    <Field label="Loan Plan" value={employee.studentLoan?.studentLoanName} />
                </Section>
                </>
            )}

            {activeTab === 'skills' && (
                <Section title="Skills & Training Matrix">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {employee.skills ? Object.entries(employee.skills).map(([key, value]) => (
                            <BooleanChip 
                                key={key} 
                                // Convert camelCase to Readable Title (e.g., dementiaTrained -> Dementia Trained)
                                label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} 
                                value={value} 
                            />
                        )) : <p>No skills data recorded.</p>}
                    </div>
                </Section>
            )}

            {activeTab === 'travel' && (
                <>
                <Section title="Vehicle Details">
                    <Field label="Registration (VRN)" value={vehicleData.vrn || vehicleData.vehicleCategory?.vrn} />
                    <Field label="Make" value={vehicleData.DVLA?.dvlaVehicleMake} />
                    <Field label="Model" value={vehicleData.DVLA?.dvlaVehicleModel} />
                    <Field label="Color" value={vehicleData.DVLA?.dvlaVehicleColor} />
                    <Field label="Fuel Type" value={vehicleData.DVLA?.dvlaVehicleFuelType} />
                    <Field label="Registered Date" value={vehicleData.DVLA?.dvlaVehicleRegistered} />
                    <Field label="MOT Status" value={vehicleData.DVLA?.latestMOTResult} />
                    <Field label="MOT Expiry" value={vehicleData.motExpiry || vehicleData.DVLA?.motExpiry} />
                    <Field label="Road Legal" value={vehicleData.roadLegal?.forceStatus} />
                    <Field label="Odometer" value={vehicleData.DVLA?.dvlaOdometerHistory} />
                </Section>
                <Section title="Insurance">
                    <Field label="Expiry Date" value={employee.carInsurance?.expiryDate} />
                    <Field label="Approved By Admin" value={employee.carInsurance?.approvedByAdmin ? "Yes" : "No"} />
                    <Field label="Document" value={employee.carInsurance?.file} isLink={true} />
                </Section>
                </>
            )}

            {activeTab === 'recruitment' && (
                <Section title="Recruitment Information">
                     <Field label="Application ID" value={employee.recruitmentInformation?.applicationID} />
                     <Field label="Application Date" value={employee.recruitmentInformation?.applicationDate} />
                     <Field label="Right to Work" value={employee.recruitmentInformation?.rightToWork} />
                     <Field label="DBS Barred List" value={employee.recruitmentInformation?.barred} />
                     <Field label="Convictions" value={employee.recruitmentInformation?.convicted} />
                     <Field label="Previous Role" value={employee.recruitmentInformation?.lastRole} />
                     <Field label="Reason for Leaving" value={employee.recruitmentInformation?.lastRoleTerminationReason} />
                     <Field label="Referee Name" value={employee.recruitmentInformation?.lastRoleRefereeName} />
                     <Field label="Referee Email" value={employee.recruitmentInformation?.lastRoleRefereeEmail} />
                </Section>
            )}

            {activeTab === 'compliance' && (
                <Section title="Documents & Checks">
                    <Field label="DBS Expiry" value={employee.dbs?.expiryDate} />
                    <Field label="DBS Link" value={employee.dbs?.updateServiceLink} />
                    <Field label="DBS File" value={employee.dbs?.file} isLink={true} />
                    
                    <div style={{ width: '100%', height: '1px', background: '#eee', margin: '15px 0' }}></div>
                    
                    <Field label="Driving License Check" value={employee.driversLicense?.dvla?.checkedAt} />
                    <Field label="Points" value={employee.driversLicense?.penaltyPoints} />
                    <Field label="License File" value={employee.driversLicense?.file} isLink={true} />
                </Section>
            )}

            {/* Edit Modal */}
            {isEditModalOpen && (
                <EditEmployeeForm 
                    employee={employee}
                    serverId={serverId}
                    closeModal={() => setIsEditModalOpen(false)}
                    onUpdate={() => window.location.reload()}
                />
            )}
        </div>
    );
}